/**
 * Pure audience derivation — deliberately free of React, so the test that
 * pins it to the real reducer can run it anywhere, and so importing it never
 * drags the provider's dependency graph along.
 */

import type { TourAudience } from './types';

/**
 * Pure audience derivation, exported so a test can drive it with the REAL
 * projects reducer and pin the store shape this depends on.
 *
 * That test exists because of how the first version of this file failed: it
 * read `state.projects.projects` — a key the reducer has never had (the slice
 * is `list`, a Record keyed by id, plus `fetched`/`total`). `Array.isArray`
 * on undefined made the fallback [] look intentional, and every user toured
 * as if they had no plans: all five in-plan stops silently filtered out, for
 * everyone, forever. The same shape assumption crashed WellnessScreen; here
 * it just quietly deleted the useful half of the tour, which is worse,
 * because nothing errored.
 */
export function deriveAudience(input: {
  projectsList: unknown;
  account: unknown;
  viaPartnerAccess: boolean;
  hasAccountChat: boolean;
}): TourAudience {
  const listRecord = (input.projectsList && typeof input.projectsList === 'object')
    ? (input.projectsList as Record<string, any>)
    : {};
  const projects = Object.values(listRecord).filter((p) => p && typeof p === 'object');
  // Oldest plan first — ids are serial, and the oldest plan is the one most
  // likely to be filled in enough for the in-plan stops to have something to
  // show. Object.values order is insertion order, which follows fetch order,
  // which is not a contract; the sort is.
  projects.sort((a: any, b: any) => Number(a?.id ?? 0) - Number(b?.id ?? 0));
  const first: any = projects[0] ?? null;

  return {
    hasProjects: projects.length > 0,
    firstProjectId: first?.id ?? null,
    firstProjectName: first?.recipient_first_name || first?.first_name || first?.name || '',
    isPartner: !!(input.account as any)?.partner,
    inPartnerAccess: !!input.viaPartnerAccess,
    hasAccountChat: input.hasAccountChat,
  };
}
